// Public types for @nostr-gives/nostr-auth.
//
// Both NIP-07 (browser extension) and NIP-46 (remote bunker) return the
// same NostrSigner shape so call sites can stay signer-agnostic. The only
// permitted signing entry point in the codebase is publishToNostr() —
// see publish.ts and Vault/Research/Nostr Safety - Follow List Hygiene.md.

export type SignerType = 'nip07' | 'nip46' | 'nsec';

export interface UnsignedNostrEvent {
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
  pubkey?: string;
}

export interface SignedNostrEvent {
  id: string;
  pubkey: string;
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
  sig: string;
}

export interface NostrSigner {
  type: SignerType;
  pubkey: string;
  signEvent(event: UnsignedNostrEvent): Promise<SignedNostrEvent>;
  disconnect?(): Promise<void>;
}

export interface AuthResult {
  pubkey: string;
  signer: NostrSigner;
}

// Frontend localStorage session shape.
export interface SessionRecord {
  pubkey: string;
  signerType: SignerType | 'anon';
  authedAt: number;
}

export class NoExtensionError extends Error {
  override readonly name = 'NoExtensionError';
  constructor(message = 'No NIP-07 extension detected on window.nostr') {
    super(message);
  }
}

export class BunkerTimeoutError extends Error {
  override readonly name = 'BunkerTimeoutError';
  constructor(message = 'Bunker did not respond before timeout') {
    super(message);
  }
}

export class BunkerConnectError extends Error {
  override readonly name = 'BunkerConnectError';
  constructor(message: string) {
    super(message);
  }
}

export class InvalidNsecError extends Error {
  override readonly name = 'InvalidNsecError';
  constructor(message = 'Invalid nsec - should start with `nsec1` and be ~63 characters') {
    super(message);
  }
}

export class KindNotAllowedError extends Error {
  override readonly name = 'KindNotAllowedError';
  constructor(kind: number) {
    super(
      `publishToNostr refuses kind ${kind}. Only 1, 30078, 27780, 27781, 30482, 30483 are permitted.`,
    );
  }
}

/**
 * Thrown by the multiplayer payload-shape chokepoint when a kind
 * 27780/27781/30482/30483 event is published with a malformed content or
 * tag set. The chokepoint sits in `@nostr-gives/puzzle-publisher`; see
 * `multiplayer-validators.ts` for the per-kind rules.
 *
 * Distinct from `KindNotAllowedError` (which rejects the kind itself).
 */
export class MultiplayerEventValidationError extends Error {
  override readonly name = 'MultiplayerEventValidationError';
  readonly kind: number;
  readonly reason: string;
  constructor(kind: number, reason: string) {
    super(`multiplayer kind ${kind} rejected: ${reason}`);
    this.kind = kind;
    this.reason = reason;
  }
}
